import React from 'react';
import { getCategory, getRequester, getUser } from './components.jsx';
// wallboard.jsx -- Big-screen IT helpdesk operations dashboard.
//
// Designed for a 30-40" wall-mounted display that the IT team can see at a
// glance. Bypasses the sidebar + topbar (renders full-screen). Reached only
// from a deliberate "Open wallboard" button on the Settings page -- not in
// the main nav, so non-team-members (or visitors looking over a shoulder)
// don't stumble into it.
//
// Strategy: feed off the existing state.tickets that the 20s background
// poll already refreshes. No new API calls -- the wallboard re-renders
// whenever the tickets list mutates upstream. Adds its own clock + a
// "tick" interval so the relative-time labels ("4h ago") stay fresh
// between data refreshes.
  const { useState, useEffect, useMemo } = React;

  // ── Helpers ──────────────────────────────────────────────────────────────
  const TODAY_START = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Age in hours from a `minsAgo` value (negative = past).
  const ageHours = (mins) => {
    if (mins == null) return 0;
    return Math.abs(mins) / 60;
  };

  // Colour-code by age. Drives the "this has been sitting too long" signal
  // -- the wallboard's whole point is making old tickets visible.
  const ageBucket = (mins) => {
    const h = ageHours(mins);
    if (h < 2)  return { label: 'FRESH',   bg: '#0F766E', fg: '#fff' };
    if (h < 24) return { label: 'TODAY',   bg: '#D97706', fg: '#fff' };
    if (h < 72) return { label: 'WARM',    bg: '#DC2626', fg: '#fff' };
    return         { label: 'STALE',   bg: '#7F1D1D', fg: '#fff' };
  };

  const fmtAge = (mins) => {
    const m = Math.abs(mins || 0);
    if (m < 60) return `${Math.round(m)}m ago`;
    if (m < 60 * 24) return `${Math.round(m / 60)}h ago`;
    return `${Math.round(m / (60 * 24))}d ago`;
  };

  // ── Sub-components ──────────────────────────────────────────────────────
  const StatCard = ({ label, value, accent, pulse }) => (
    <div style={{
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 16,
      padding: '24px 32px',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      minHeight: 140,
      justifyContent: 'center',
    }}>
      <div style={{
        fontSize: 14,
        fontWeight: 700,
        letterSpacing: 1.5,
        color: 'rgba(255,255,255,0.5)',
        textTransform: 'uppercase',
      }}>{label}</div>
      <div style={{
        fontSize: 88,
        fontWeight: 800,
        lineHeight: 1,
        color: accent || '#fff',
        fontFeatureSettings: '"tnum"',
        // Pulse animation when the value passes a "needs attention" threshold
        // -- meant to catch your eye across the room when a new unassigned
        // ticket arrives during the 20s gap between polls.
        animation: pulse ? 'triage-pulse 2s ease-in-out infinite' : 'none',
      }}>{value}</div>
    </div>
  );

  // Priority pill colours match the existing app's priority hues.
  const PRIORITY_STYLE = {
    urgent: { bg: '#7F1D1D', fg: '#FCA5A5', label: 'URGENT' },
    high:   { bg: '#9A3412', fg: '#FED7AA', label: 'HIGH' },
    medium: { bg: '#92400E', fg: '#FDE68A', label: 'MED' },
    low:    { bg: '#1E3A8A', fg: '#BFDBFE', label: 'LOW' },
  };

  // Strip the boilerplate that always sits at the top of email-imported
  // bodies so the description preview shows the actual content.
  const cleanDescription = (raw) => {
    if (!raw) return '';
    return raw
      .replace(/^[\s>]*From:.+$/gim, '')
      .replace(/^[\s>]*Sent:.+$/gim, '')
      .replace(/^[\s>]*To:.+$/gim, '')
      .replace(/^[\s>]*Subject:.+$/gim, '')
      .replace(/^[\s>]*Cc:.+$/gim, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const TicketCard = ({ ticket, getRequester }) => {
    const age = ageBucket(ticket.created);
    const requester = ticket.requesterName || (getRequester(ticket.requester)?.name) || ticket.requester || '-';
    const pri = PRIORITY_STYLE[ticket.priority] || PRIORITY_STYLE.medium;
    const cat = getCategory && getCategory(ticket.category)?.label;
    const desc = cleanDescription(ticket.messages?.[0]?.body || '');
    return (
      <div style={{
        display: 'flex',
        gap: 14,
        padding: '16px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        // Subtle left accent matching age - colour-code the row edge so the
        // queue's age distribution reads at a distance.
        borderLeft: `4px solid ${age.bg}`,
        background: 'rgba(255,255,255,0.015)',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title line: ID + subject */}
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 10,
            marginBottom: 4,
          }}>
            <span style={{
              fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.45)',
              fontFeatureSettings: '"tnum"',
            }}>#{ticket.id}</span>
            <span style={{
              fontSize: 22, fontWeight: 600, color: '#fff',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              flex: 1,
            }}>{ticket.subject}</span>
          </div>
          {/* Description preview - one line, ellipsis */}
          {desc && (
            <div style={{
              fontSize: 14, color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.4, marginBottom: 6,
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>{desc}</div>
          )}
          {/* Meta row: requester · category · age */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            fontSize: 13, color: 'rgba(255,255,255,0.5)',
            flexWrap: 'wrap',
          }}>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>{requester}</span>
            {cat && (
              <>
                <span>·</span>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px',
                  background: 'rgba(255,255,255,0.08)', borderRadius: 4,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>{cat}</span>
              </>
            )}
            <span>·</span>
            <span>{fmtAge(ticket.created)}</span>
          </div>
        </div>
        {/* Right column: priority pill on top, age stamp below */}
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'flex-end', gap: 6, flexShrink: 0,
        }}>
          <span style={{
            background: pri.bg, color: pri.fg,
            fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
            padding: '4px 10px', borderRadius: 4,
          }}>{pri.label}</span>
          <span style={{
            background: age.bg, color: age.fg,
            fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
            padding: '3px 9px', borderRadius: 4,
          }}>{age.label}</span>
        </div>
      </div>
    );
  };

  // Continuous slow scroll for lists that overflow their container. Works by
  // duplicating the children and animating the inner div by -50% - when the
  // first copy scrolls fully off the top, the second copy is right where
  // the first started, giving a seamless loop. No JS interval, no jank.
  //
  // Only enables when content actually overflows (measured on mount + when
  // children change). If everything fits, just renders normally with no
  // animation - saves the visual noise on light days.
  //
  // Pauses on hover via CSS so someone walking up can stop the scroll
  // and read whatever's mid-screen.
  function ScrollLoop({ children, secondsPerItem = 5, fadeEdges = true }) {
    const containerRef = React.useRef(null);
    const contentRef = React.useRef(null);
    const [animDuration, setAnimDuration] = React.useState(0);

    // Re-measure when children change. ResizeObserver covers the layout
    // shifts that happen as ticket rows mount with their async data
    // (priority pills, category labels, etc).
    React.useEffect(() => {
      const measure = () => {
        const c = containerRef.current, inner = contentRef.current;
        if (!c || !inner) return;
        const containerH = c.clientHeight;
        // The inner contains TWO copies of children (rendered below); we
        // measure the first copy via the first-child's scrollHeight sum.
        // Easier: use half the inner's scrollHeight, since the two copies
        // are identical.
        const halfH = inner.scrollHeight / 2;
        if (halfH > containerH + 20) {
          // Items count via first half - children may be a fragment so use
          // the rendered DOM children of the first batch container.
          const itemCount = inner.firstChild
            ? inner.firstChild.children.length
            : React.Children.count(children);
          setAnimDuration(Math.max(15, itemCount * secondsPerItem));
        } else {
          setAnimDuration(0);   // disable - content fits
        }
      };
      measure();
      const obs = new ResizeObserver(measure);
      if (contentRef.current) obs.observe(contentRef.current);
      if (containerRef.current) obs.observe(containerRef.current);
      return () => obs.disconnect();
    }, [children, secondsPerItem]);

    const animating = animDuration > 0;
    const maskStyle = fadeEdges ? {
      WebkitMaskImage: 'linear-gradient(180deg, transparent 0, #000 36px, #000 calc(100% - 36px), transparent 100%)',
      maskImage: 'linear-gradient(180deg, transparent 0, #000 36px, #000 calc(100% - 36px), transparent 100%)',
    } : {};
    return (
      <div ref={containerRef} className="scroll-loop" style={{
        flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0,
        ...maskStyle,
      }}>
        <div ref={contentRef} style={{
          animation: animating ? `triage-scroll ${animDuration}s linear infinite` : 'none',
        }}>
          {/* First copy */}
          <div>{children}</div>
          {/* Second copy renders only when animating - wasted DOM otherwise */}
          {animating && <div aria-hidden="true">{children}</div>}
        </div>
      </div>
    );
  }

  // ── Main view ───────────────────────────────────────────────────────────
  function WallboardView({ state, dispatch }) {
    const [now, setNow] = useState(() => new Date());
    // Wallboard needs the FULL tickets list, not the paginated slice in
    // state.tickets (that's now just the current tickets-list page).
    // Dedicated fetch on mount + 30s refresh so the big-screen view stays
    // representative of the actual queue. Uses ?size=500 which covers even
    // a busy month; if the queue ever legitimately exceeds that we should
    // switch to a dedicated /wallboard endpoint that computes the tiles
    // server-side rather than shipping the whole dataset.
    const [tickets, setTickets] = useState([]);
    useEffect(() => {
      let cancelled = false;
      const load = () => {
        window.IT_AUTH.api('GET', '/tickets?view=all&size=500', undefined, 'wallboard')
          .then(r => { if (!cancelled && Array.isArray(r?.rows)) setTickets(r.rows); })
          .catch(err => console.warn('[wallboard] fetch failed:', err.message));
      };
      load();
      const id = setInterval(load, 30000);
      return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Tick every second so the clock + relative times stay current. Data
    // freshness comes from the dedicated wallboard fetch above.
    useEffect(() => {
      const id = setInterval(() => setNow(new Date()), 1000);
      return () => clearInterval(id);
    }, []);

    // Press Esc to exit the wallboard back to home (handy when controlling
    // from a keyboard rather than a touchscreen).
    useEffect(() => {
      const onKey = (e) => {
        if (e.key === 'Escape') dispatch({ type: 'navigate', view: 'home' });
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [dispatch]);
    const todayStart = useMemo(() => TODAY_START(), [now.toDateString()]);

    // Derive everything from state.tickets - no new fetches.
    const open       = tickets.filter(t => !t.trashed && t.status !== 'resolved');
    const unassigned = open.filter(t => !t.assignee).sort((a,b) => (a.created ?? 0) - (b.created ?? 0));
    const closedToday = tickets.filter(t => {
      if (t.status !== 'resolved' || t.closedAt == null) return false;
      const closeMs = Date.now() + t.closedAt * 60 * 1000;
      return closeMs >= todayStart.getTime();
    });

    // Today's tally - tickets created today AND closed today. Quick health
    // signal: are we keeping pace, or is the queue growing?
    const createdToday = tickets.filter(t => {
      if (t.trashed || t.created == null) return false;
      const createdMs = Date.now() + t.created * 60 * 1000;
      return createdMs >= todayStart.getTime();
    });

    // Needs-attention: stale open tickets (older than 3 days, not resolved).
    // These are the ones that have probably slipped through the cracks.
    const staleOpen = open
      .filter(t => ageHours(t.created) > 72)
      .sort((a, b) => (a.created ?? 0) - (b.created ?? 0));   // oldest first
    const oldestUnresolved = open
      .slice()
      .sort((a, b) => (a.created ?? 0) - (b.created ?? 0))[0];

    // Priority breakdown for unassigned - quick "is anything urgent waiting"
    // signal so the team can prioritise visually.
    const unassignedByPri = {
      urgent: unassigned.filter(t => t.priority === 'urgent').length,
      high:   unassigned.filter(t => t.priority === 'high').length,
      medium: unassigned.filter(t => t.priority === 'medium').length,
      low:    unassigned.filter(t => t.priority === 'low').length,
    };

    // Recent activity - last 8 ticket events. Closures + new (last 6h) tickets.
    const activityFeed = useMemo(() => {
      const events = [];
      for (const t of tickets) {
        if (t.trashed) continue;
        if (t.status === 'resolved' && t.closedAt != null && ageHours(t.closedAt) < 6) {
          events.push({
            kind: 'closed', time: t.closedAt, ticket: t,
            text: `Closed #${t.id} ${t.subject}`,
            actor: t.assignee,
          });
        }
        if (t.created != null && ageHours(t.created) < 6 && t.status !== 'resolved') {
          events.push({
            kind: 'new', time: t.created, ticket: t,
            text: `New #${t.id} ${t.subject}`,
            actor: null,
          });
        }
      }
      return events
        .sort((a, b) => Math.abs(a.time ?? 0) - Math.abs(b.time ?? 0))
        .slice(0, 8);
    }, [tickets]);

    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'linear-gradient(180deg, #0a1220 0%, #0f1a2e 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        zIndex: 9999,
        overflow: 'hidden',
      }}>
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '22px 36px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          {/* Triage logo - using the inline SVG version that already lives in
              the sidebar. Re-rendering it here so the wallboard reads as the
              firm's IT system at a glance, not "a generic dashboard". */}
          <svg viewBox="0 0 240 96" xmlns="http://www.w3.org/2000/svg" style={{ height: 50, marginRight: 18 }}>
            <rect x="24" y="24" width="30" height="23" rx="5" fill="#0D9488" fillOpacity="0.35"/>
            <rect x="16" y="34" width="30" height="23" rx="5" fill="#0D9488"/>
            <text x="62" y="48" fontFamily="Poppins, sans-serif" fontWeight="800" fontSize="36" fill="#0D9488">TRI</text>
            <text x="124" y="48" fontFamily="Poppins, sans-serif" fontWeight="800" fontSize="36" fill="#fff">AGE</text>
            <text x="62" y="68" fontFamily="Poppins, sans-serif" fontWeight="500" fontSize="11" fill="rgba(255,255,255,0.5)" letterSpacing="2">IT HELPDESK</text>
          </svg>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 0.5 }}>Operations Wallboard</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
              Lee &amp; Thompson · auto-refresh every 20s · press <kbd style={{
                background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: 4,
                fontFamily: 'inherit', fontSize: 12, marginLeft: 2,
              }}>Esc</kbd> to exit
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 38, fontWeight: 800, fontFeatureSettings: '"tnum"', lineHeight: 1.1 }}>
              {now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
              {now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
        </div>

        {/* ── Stat row ───────────────────────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 24,
          padding: '24px 36px',
        }}>
          <StatCard label="Open tickets" value={open.length} accent="#fff"/>
          <StatCard
            label="Unassigned"
            value={unassigned.length}
            accent={unassigned.length > 0 ? '#FCA5A5' : 'rgba(255,255,255,0.6)'}
          />
          <StatCard label="Closed today" value={closedToday.length} accent="#86EFAC"/>
        </div>

        {/* ── Main two-column section ────────────────────────────────── */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 24,
          padding: '0 36px 24px',
          minHeight: 0,
        }}>
          {/* Unassigned queue - the actionable list. Bigger and richer than
              v1 - each card shows priority pill, category, requester, age,
              and a one-line description preview, with a coloured left edge
              keyed to the ticket's age bucket. */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}>
            <div style={{
              padding: '18px 22px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
              <div style={{
                fontSize: 14, fontWeight: 800, letterSpacing: 1.5,
                color: '#FCA5A5', textTransform: 'uppercase',
              }}>Unassigned queue</div>
              <div style={{
                fontSize: 12, color: 'rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.08)',
                padding: '2px 10px', borderRadius: 10, fontWeight: 600,
              }}>{unassigned.length}</div>
              {/* Inline priority breakdown when there's anything urgent/high */}
              {(unassignedByPri.urgent + unassignedByPri.high) > 0 && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, fontSize: 11, fontWeight: 700 }}>
                  {unassignedByPri.urgent > 0 && (
                    <span style={{ background: '#7F1D1D', color: '#FCA5A5', padding: '2px 8px', borderRadius: 4 }}>
                      {unassignedByPri.urgent} URGENT
                    </span>
                  )}
                  {unassignedByPri.high > 0 && (
                    <span style={{ background: '#9A3412', color: '#FED7AA', padding: '2px 8px', borderRadius: 4 }}>
                      {unassignedByPri.high} HIGH
                    </span>
                  )}
                </div>
              )}
            </div>
            {unassigned.length === 0 ? (
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'rgba(255,255,255,0.4)', fontSize: 22, fontWeight: 500,
              }}>
                ✓ Queue is clear
              </div>
            ) : (
              /* All unassigned shown - ScrollLoop handles overflow with a
                 slow seamless scroll. No artificial slice - the team should
                 see everything that's waiting. */
              <ScrollLoop secondsPerItem={6}>
                {unassigned.map(t => (
                  <TicketCard key={t.id} ticket={t} getRequester={getRequester}/>
                ))}
              </ScrollLoop>
            )}
          </div>

          {/* Right column: Today, Needs attention, Recent activity */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minHeight: 0 }}>
            {/* Today - created vs closed running tally */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 16,
              padding: '18px 22px',
            }}>
              <div style={{
                fontSize: 14, fontWeight: 800, letterSpacing: 1.5,
                color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase',
                marginBottom: 14,
              }}>Today</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                <div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Created</div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#FCA5A5', fontFeatureSettings: '"tnum"', lineHeight: 1 }}>
                    {createdToday.length}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Closed</div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#86EFAC', fontFeatureSettings: '"tnum"', lineHeight: 1 }}>
                    {closedToday.length}
                  </div>
                </div>
              </div>
              {/* Net signal: are we keeping pace? */}
              {(createdToday.length > 0 || closedToday.length > 0) && (() => {
                const net = closedToday.length - createdToday.length;
                const netColor = net > 0 ? '#86EFAC' : net < 0 ? '#FCA5A5' : 'rgba(255,255,255,0.5)';
                const netLabel = net > 0 ? `+${net} ahead` : net < 0 ? `${net} behind` : 'even';
                return (
                  <div style={{ marginTop: 12, fontSize: 13, color: netColor, fontWeight: 600 }}>
                    {netLabel}
                  </div>
                );
              })()}
            </div>

            {/* Needs attention - oldest unresolved + stale count */}
            <div style={{
              background: staleOpen.length > 0 ? 'rgba(127, 29, 29, 0.25)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${staleOpen.length > 0 ? '#7F1D1D' : 'rgba(255,255,255,0.06)'}`,
              borderRadius: 16,
              padding: '18px 22px',
            }}>
              <div style={{
                fontSize: 14, fontWeight: 800, letterSpacing: 1.5,
                color: staleOpen.length > 0 ? '#FCA5A5' : 'rgba(255,255,255,0.7)',
                textTransform: 'uppercase',
                marginBottom: 12,
              }}>Needs attention</div>
              {oldestUnresolved ? (
                <>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
                    Oldest open
                  </div>
                  <div style={{
                    fontSize: 16, fontWeight: 600, color: '#fff',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    #{oldestUnresolved.id} {oldestUnresolved.subject}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                    {fmtAge(oldestUnresolved.created)}
                  </div>
                  {staleOpen.length > 0 && (
                    <div style={{
                      marginTop: 14, paddingTop: 14,
                      borderTop: '1px solid rgba(255,255,255,0.08)',
                      fontSize: 14, color: 'rgba(255,255,255,0.7)',
                    }}>
                      <span style={{ color: '#FCA5A5', fontWeight: 700, fontSize: 18 }}>
                        {staleOpen.length}
                      </span>
                      {' '}open longer than 3 days
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
                  Nothing open. Inbox zero.
                </div>
              )}
            </div>

            {/* Recent activity */}
            <div style={{
              flex: 1,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 16,
              padding: '18px 22px',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}>
              <div style={{
                fontSize: 14, fontWeight: 800, letterSpacing: 1.5,
                color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase',
                marginBottom: 12,
              }}>Recent activity</div>
              {activityFeed.length === 0 ? (
                <div style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'rgba(255,255,255,0.4)', fontSize: 16,
                }}>
                  Nothing in the last 6 hours.
                </div>
              ) : (
                <ScrollLoop secondsPerItem={4}>
                  {activityFeed.map((e, i) => {
                    const actor = e.actor ? getUser(e.actor) : null;
                    const isNew = e.kind === 'new';
                    return (
                      <div key={`${e.kind}-${e.ticket.id}-${i}`} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 0',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                      }}>
                        <span style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: isNew ? '#3B82F6' : '#10B981',
                          flexShrink: 0,
                          boxShadow: `0 0 0 3px ${isNew ? 'rgba(59,130,246,0.2)' : 'rgba(16,185,129,0.2)'}`,
                        }}/>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 15, color: '#fff', fontWeight: 500,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {actor && <span style={{ color: actor.color || '#0D9488', fontWeight: 700 }}>{actor.name?.split(' ')[0]} </span>}
                            {e.text}
                          </div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                            {fmtAge(e.time)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </ScrollLoop>
              )}
            </div>
          </div>
        </div>

        {/* Inline keyframes for the pulse animation. Local style tag so it
            doesn't bleed into the rest of the app's CSS. */}
        <style>{`
          @keyframes triage-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.75; transform: scale(1.06); }
          }
          /* Seamless loop: the children are duplicated inside ScrollLoop,
             so translating -50% lands the second copy exactly where the
             first started. Linear timing gives the eye a constant pace. */
          @keyframes triage-scroll {
            0%   { transform: translateY(0); }
            100% { transform: translateY(-50%); }
          }
          /* Pause when someone walks up and hovers - they can read the
             content without having to wait for the wraparound. */
          .scroll-loop:hover > div { animation-play-state: paused !important; }
        `}</style>
      </div>
    );
  }

export { WallboardView };